import React from 'react';
import {message, Modal} from 'antd';
import {
	findViewsContainingConcept,
	getRelationshipEndpointIds,
} from '@/utils/diagram/findViewsContainingConcept';
import {getViewLabel} from '@/utils/diagram/getViewLabel';
import {isCanvasOnlyNodeType} from '@core_x6/lib/util/CanvasNodeTypes';
import {projectsApi} from '@/api/projects';

function selectConceptInModeler(modeler, conceptId, isRelationship) {
	try {
		if (modeler.selectConcept) modeler.selectConcept(conceptId, isRelationship);
	} catch (e) {
		console.warn('[ArchiX6] selectConcept failed:', e);
	}
}

async function placeLinkedCatalogRelationships(modeler, catalog, newElementRef) {
	if (!modeler || !catalog?.projectId || !newElementRef) return 0;
	const ends = modeler.collectElementRefsOnCurrentView?.() ?? [];
	if (ends.length < 2) return 0;
	const res = await projectsApi.searchCatalogRelationships(catalog.projectId, {
		ends,
	});
	if (res.error || !Array.isArray(res.data)) return 0;
	let placed = 0;
	for (const rel of res.data) {
		if (rel?.source !== newElementRef && rel?.target !== newElementRef) continue;
		const result = modeler.placeCatalogRelationshipOnView?.(rel);
		if (result?.added) placed += 1;
	}
	return placed;
}

/**
 * Обработчики навигации по views, дерева модели и контекстного меню ArchiX6.
 */
export function useArchiX6DiagramHandlers({
	modelerRef,
	syncViews,
	views,
	activeViewId,
	setActiveViewId,
	onViewChange,
	onApplyPatch,
	onSave,
	navigateViewPickerConceptRef,
	setNavigateViewPickerOpen,
	setNavigateViewPickerList,
	setViewModalMode,
	setViewModalName,
	setViewModalEditingId,
	setPropertiesDrawerOpen,
	setPropertiesDrawerElement,
	setViewpointModalOpen,
	viewModalMode,
	viewModalName,
	viewModalEditingId,
	setSelectionMode,
	catalog,
}) {
	const save = React.useCallback(async () => {
		if (!modelerRef.current) return;
		if (onApplyPatch) {
			message.success('Изменения сохраняются автоматически');
			return;
		}
		if (!onSave) return;
		try {
			const {json} = await modelerRef.current.saveJSON();
			await onSave(typeof json === 'string' ? json : JSON.stringify(json));
			message.success('Сохранено');
		} catch {
			message.error('Ошибка сохранения');
		}
	}, [onSave, onApplyPatch, modelerRef]);

	const applyViewpointFromModal = React.useCallback(
		val => {
			const modeler = modelerRef.current;
			const viewId = modeler?.getActiveViewId?.();
			if (viewId) {
				modeler.setActiveViewViewpoint?.(val);
				if (onApplyPatch) {
					onApplyPatch({
						patches: [
							{
								op: 'updateViewpoint',
								viewIdentifier: viewId,
								viewIndex: modeler.getActiveViewIndex?.(),
								viewpoint: val,
							},
						],
					});
				}
			}
			setViewpointModalOpen(false);
		},
		[onApplyPatch, modelerRef, setViewpointModalOpen]
	);

	const onViewSelect = React.useCallback(
		(viewId, opts) => {
			const modeler = modelerRef.current;
			if (!modeler) return Promise.resolve();
			setSelectionMode(false);
			modeler.disablePaletteBoxSelectMode?.();
			return modeler
				.openView(viewId)
				.then(() => {
					setActiveViewId(viewId);
					onViewChange?.(viewId);
					if (opts?.selectConceptId != null && modeler.selectConcept) {
						modeler.selectConcept(
							opts.selectConceptId,
							opts.selectConceptIsRelationship
						);
					} else {
						requestAnimationFrame(() => {
							modeler.fitViewport?.();
						});
					}
				})
				.catch(err => {
					void message.error('Ошибка при открытии представления');
					console.error(err);
				});
		},
		[modelerRef, setActiveViewId, onViewChange, setSelectionMode]
	);

	const deleteConceptFromView = React.useCallback(
		(conceptId, isRelationship) => {
			const modeler = modelerRef.current;
			if (!modeler) return;
			Modal.confirm({
				title: 'Удалить с диаграммы?',
				content: 'Элемент будет удалён только с текущей диаграммы (не из модели).',
				okText: 'Удалить',
				okType: 'danger',
				cancelText: 'Отмена',
				onOk: () => {
					const viewElement = modeler._findViewElementByConceptId?.(
						conceptId,
						isRelationship
					);
					if (!viewElement) {
						void message.warning('Элемент не найден на текущей диаграмме');
						return;
					}
					const viewElementId = viewElement.id ?? viewElement.$attrs?.id;
					if (!viewElementId) return;
					const removed = modeler.removeConceptFromView?.(conceptId, isRelationship);
					if (removed) {
						syncViews();
						void message.success('Элемент удалён с диаграммы');
						if (onApplyPatch) {
							const viewList = modeler.getViews?.() ?? [];
							const viewIndex = viewList.findIndex(
								v => (v?.id ?? v?.$attrs?.id) === modeler.getActiveViewId?.()
							);
							onApplyPatch({
								patches: [
									{
										op: isRelationship ? 'removeConnection' : 'removeNode',
										viewIndex: viewIndex >= 0 ? viewIndex : 0,
										identifier: viewElementId,
									},
								],
							});
						}
					} else {
						void message.warning('Не удалось удалить элемент');
					}
				},
			});
		},
		[modelerRef, syncViews, onApplyPatch]
	);

	const addConceptToView = React.useCallback(
		(conceptId, isRelationship, catalogHint) => {
			const modeler = modelerRef.current;
			if (!modeler) return;
			const result = modeler.addConceptToView?.(conceptId, isRelationship, catalogHint);
			if (result?.alreadyOnView) {
				void message.info(
					isRelationship ? 'Связь уже на диаграмме' : 'Элемент уже на диаграмме'
				);
				return;
			}
			if (result?.missingEndpoints) {
				void message.warning('Сначала добавьте на диаграмму оба конца связи');
				return;
			}
			if (result?.added) {
				syncViews();
				void message.success(
					isRelationship ? 'Связь добавлена на диаграмму' : 'Элемент добавлен на диаграмму'
				);
				if (!isRelationship) {
					void placeLinkedCatalogRelationships(modeler, catalog, conceptId).then(
						placed => {
							if (placed > 0) syncViews();
						}
					);
				}
			} else {
				void message.warning(
					isRelationship ? 'Не удалось добавить связь' : 'Не удалось добавить элемент'
				);
			}
		},
		[modelerRef, syncViews, catalog]
	);

	const isConceptOnCurrentView = React.useCallback(
		(conceptId, isRelationship) => {
			const modeler = modelerRef.current;
			if (!modeler) return false;
			const model = modeler.getModel?.();
			const activeId = modeler.getActiveViewId?.();
			if (!model || !activeId) return false;
			const list = findViewsContainingConcept(model, conceptId, isRelationship);
			return list.some(v => v.viewId === activeId);
		},
		[modelerRef]
	);

	const isConceptOnAnyView = React.useCallback(
		(conceptId, isRelationship) => {
			const modeler = modelerRef.current;
			if (!modeler) return true;
			const model = modeler.getModel?.();
			if (!model) return true;
			const list = findViewsContainingConcept(model, conceptId, isRelationship);
			return list.length > 0;
		},
		[modelerRef]
	);

	const deleteConceptFromModel = React.useCallback(
		(conceptId, isRelationship) => {
			const modeler = modelerRef.current;
			if (!modeler) return;
			Modal.confirm({
				title: 'Удалить из модели?',
				content: 'Элемент будет удалён из всех диаграмм.',
				okText: 'Удалить',
				okType: 'danger',
				cancelText: 'Отмена',
				onOk: () => {
					try {
						const op = isRelationship
							? 'removeRelationshipFromModel'
							: 'removeElementFromModel';
						const patch = {patches: [{op, identifier: conceptId}]};
						modeler.applyModelPatch?.(patch);
						if (onApplyPatch) onApplyPatch(patch);
						syncViews();
						void message.success('Элемент удалён из модели');
					} catch (e) {
						console.warn('[ArchiX6] deleteConceptFromModel failed:', e);
						void message.error('Ошибка удаления');
					}
				},
			});
		},
		[modelerRef, onApplyPatch, syncViews]
	);

	const onNavigateToConcept = React.useCallback(
		(conceptId, isRelationship) => {
			const modeler = modelerRef.current;
			const model = modeler?.getModel?.();
			if (!model) {
				return;
			}

			let found = findViewsContainingConcept(model, conceptId, isRelationship);

			if (found.length === 0 && isRelationship) {
				const endpoints = getRelationshipEndpointIds(model, conceptId);
				if (endpoints) {
					const fallbackSource = endpoints.sourceId
						? findViewsContainingConcept(model, endpoints.sourceId, false)
						: [];

					const fallbackTarget = endpoints.targetId
						? findViewsContainingConcept(model, endpoints.targetId, false)
						: [];

					const fallback = fallbackSource[0] ?? fallbackTarget[0];

					if (fallback) {
						const elemId = fallbackSource[0] ? endpoints.sourceId : endpoints.targetId;
						if (fallback.viewId === activeViewId) {
							selectConceptInModeler(modeler, elemId, false);
						} else {
							onViewSelect(fallback.viewId, {
								selectConceptId: elemId,
								selectConceptIsRelationship: false,
							});
						}
						return;
					}
				}
			}

			if (found.length === 0) {
				void message.info('Элемент не найден на диаграммах');
				return;
			}

			if (found.length === 1) {
				const targetViewId = found[0].viewId;
				if (targetViewId === activeViewId) {
					selectConceptInModeler(modeler, conceptId, isRelationship);
					return;
				}

				onViewSelect(targetViewId, {
					selectConceptId: conceptId,
					selectConceptIsRelationship: isRelationship,
				});

				return;
			}

			navigateViewPickerConceptRef.current = {conceptId, isRelationship};
			setNavigateViewPickerList(found);
			setNavigateViewPickerOpen(true);
		},
		[
			modelerRef,
			activeViewId,
			onViewSelect,
			navigateViewPickerConceptRef,
			setNavigateViewPickerList,
			setNavigateViewPickerOpen,
		]
	);

	const onNavigateViewPickerSelect = React.useCallback(
		(viewId, conceptId, isRelationship) => {
			setNavigateViewPickerOpen(false);
			setNavigateViewPickerList([]);
			const modeler = modelerRef.current;
			const opts =
				conceptId != null
					? {selectConceptId: conceptId, selectConceptIsRelationship: isRelationship}
					: undefined;

			if (viewId !== activeViewId) {
				onViewSelect(viewId, opts);
			} else if (modeler && conceptId != null) {
				selectConceptInModeler(modeler, conceptId, isRelationship);
			}
		},
		[
			modelerRef,
			activeViewId,
			onViewSelect,
			setNavigateViewPickerOpen,
			setNavigateViewPickerList,
		]
	);

	const onDeleteView = React.useCallback(
		viewId => {
			const modeler = modelerRef.current;
			if (!modeler) {
				return;
			}

			const idToDelete = viewId ?? activeViewId;

			if (!idToDelete) {
				return;
			}

			const list = modeler.getViews?.() ?? [];

			if (!(list.length > 1)) {
				return;
			}

			const view = list.find(v => (v?.id ?? v?.$attrs?.id) === idToDelete);
			const viewLabel = getViewLabel(view ?? {});

			Modal.confirm({
				title: 'Удалить диаграмму?',
				content: `Диаграмма «${viewLabel}» будет удалена. Это действие нельзя отменить.`,
				okText: 'Удалить',
				okType: 'danger',
				cancelText: 'Отмена',
				onOk: () =>
					modeler
						.deleteView(idToDelete)
						.then(() => {
							syncViews();
							const nextId = modeler.getActiveViewId?.();
							setActiveViewId(nextId);
							onViewChange?.(nextId);
							void message.success('Диаграмма удалена');

							if (onApplyPatch) {
								onApplyPatch({
									patches: [{op: 'removeView', viewIdentifier: idToDelete}],
								});
							}
						})
						.catch(err => message.error(err?.message || 'Ошибка удаления')),
			});
		},
		[modelerRef, activeViewId, syncViews, setActiveViewId, onViewChange, onApplyPatch]
	);

	const onAddView = React.useCallback(() => {
		setViewModalMode('create');

		const suggested = views.length ? `Диаграмма ${views.length + 1}` : 'Диаграмма 1';

		setViewModalName(suggested);
		setViewModalEditingId(null);
	}, [views.length, setViewModalMode, setViewModalName, setViewModalEditingId]);

	const onEditView = React.useCallback(
		viewId => {
			const modeler = modelerRef.current;
			if (!modeler) {
				return;
			}

			const idToEdit = viewId ?? activeViewId;

			if (!idToEdit) {
				return;
			}

			const list = modeler.getViews?.() ?? [];
			const view = list.find(v => (v?.id ?? v?.$attrs?.id) === idToEdit);

			if (!view) {
				return;
			}

			setViewModalMode('edit');
			setViewModalName(getViewLabel(view));
			setViewModalEditingId(idToEdit);
		},
		[modelerRef, activeViewId, setViewModalMode, setViewModalName, setViewModalEditingId]
	);

	const onViewModalOk = React.useCallback(() => {
		const modeler = modelerRef.current;
		if (!modeler || !viewModalName.trim()) {
			return;
		}

		const name = viewModalName.trim();

		if (viewModalMode === 'create') {
			modeler
				.createView(name)
				.then(({view}) => {
					syncViews();
					const newId = view?.id ?? view?.$attrs?.id ?? modeler.getActiveViewId?.();

					setActiveViewId(newId);
					onViewChange?.(newId);
					setViewModalMode(null);

					void message.success('Диаграмма создана');

					if (onApplyPatch && view) {
						const viewId = view.id ?? view.$attrs?.id;
						if (viewId) {
							onApplyPatch({
								patches: [{op: 'addView', viewIdentifier: viewId, viewName: name}],
							});
						}
					}
				})
				.catch(err => message.error(err?.message || 'Ошибка создания диаграммы'));
			return;
		}

		if (viewModalMode !== 'edit' || !viewModalEditingId) {
			return;
		}

		const viewsList = modeler.getModel?.()?.views?.diagrams?.viewsList ?? [];

		const view = viewsList.find(v => (v?.id ?? v?.$attrs?.id) === viewModalEditingId);

		if (!view) {
			return;
		}

		if (view.set) {
			view.set('name', name);
		} else {
			view.name = name;
		}

		syncViews();
		setViewModalMode(null);

		void message.success('Название сохранено');

		if (onApplyPatch) {
			onApplyPatch({
				patches: [
					{
						op: 'updateViewName',
						viewIdentifier: viewModalEditingId,
						viewName: name,
					},
				],
			});
		}
	}, [
		modelerRef,
		viewModalMode,
		viewModalName,
		viewModalEditingId,
		syncViews,
		setActiveViewId,
		onViewChange,
		setViewModalMode,
		onApplyPatch,
	]);

	const onSettingsClick = React.useCallback(() => {
		void message.info('Настройки пока недоступны');
	}, []);

	const openPropertiesFromContextMenu = React.useCallback(
		cellId => {
			const modeler = modelerRef.current;
			const viewElement = modeler?._findViewElementById?.(cellId);

			if (!viewElement) return;

			setPropertiesDrawerElement({
				businessObject: viewElement,
				id: viewElement?.id ?? viewElement?.$attrs?.id,
			});

			setPropertiesDrawerOpen(true);
		},
		[modelerRef, setPropertiesDrawerElement, setPropertiesDrawerOpen]
	);

	const deleteNodeFromModelByCellId = React.useCallback(
		cellId => {
			const modeler = modelerRef.current;
			if (modeler?.isLockedByOther?.(cellId)) {
				void message.warning('Элемент сейчас редактирует другой участник');
				return;
			}
			const viewElement = modeler?._findViewElementById?.(cellId);
			if (isCanvasOnlyNodeType(viewElement?.type)) {
				Modal.confirm({
					title: 'Удалить стикер?',
					content: 'Стикер будет удалён полностью.',
					okText: 'Удалить',
					okType: 'danger',
					cancelText: 'Отмена',
					onOk: () => {
						modeler?.removeNodeFromView?.(cellId);
						void message.success('Стикер удалён');
					},
				});
				return;
			}
			const elementRef = viewElement ? modeler?._getElementRef?.(viewElement) : null;
			const conceptId = elementRef?.id ?? elementRef?.$attrs?.id ?? null;

			if (!conceptId) {
				void message.info('Этот элемент нельзя удалить из модели');
				return;
			}

			deleteConceptFromModel(conceptId, false);
		},
		[modelerRef, deleteConceptFromModel]
	);

	return {
		save,
		applyViewpointFromModal,
		onViewSelect,
		deleteConceptFromView,
		addConceptToView,
		isConceptOnCurrentView,
		isConceptOnAnyView,
		deleteConceptFromModel,
		onNavigateToConcept,
		onNavigateViewPickerSelect,
		onDeleteView,
		onAddView,
		onEditView,
		onViewModalOk,
		onSettingsClick,
		openPropertiesFromContextMenu,
		deleteNodeFromModelByCellId,
	};
}
